import { useEffect, useRef, useState } from "react";
import { ChevronDown, Globe } from "lucide-react";

const LANGS = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "zh-CN", label: "中文", dir: "ltr" },
];

const getCurrentCode = () => {
  const match = document.cookie.match(/googtrans=\/en\/([a-zA-Z-]+)/);
  return match ? match[1] : "en";
};

const LanguageSwitcher = ({ mobile = false, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("en");
  const ref = useRef(null);

  useEffect(() => {
    setCurrent(getCurrentCode());
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Google Translate reads/writes the `googtrans` cookie on several domain
  // scopes (host-only, `.host`, and each parent domain e.g. `.emergentagent.com`).
  // We must clear AND set every scope consistently, otherwise a stale broader-scope
  // cookie (e.g. from a previous language) conflicts with the new one and Google
  // Translate refuses to translate — this is what broke switching between two
  // non-English languages.
  const domainVariants = () => {
    const host = window.location.hostname;
    const parts = host.split(".");
    const variants = new Set();
    variants.add(""); // host-only (no domain attribute)
    variants.add(host);
    variants.add(`.${host}`);
    for (let i = 1; i < parts.length - 1; i += 1) {
      variants.add(`.${parts.slice(i).join(".")}`);
    }
    return Array.from(variants);
  };

  const clearGoogtransCookies = () => {
    domainVariants().forEach((d) => {
      const domainPart = d ? `;domain=${d}` : "";
      document.cookie = `googtrans=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainPart}`;
    });
  };

  const setGoogtransCookies = (value) => {
    domainVariants().forEach((d) => {
      const domainPart = d ? `;domain=${d}` : "";
      document.cookie = `googtrans=${value};path=/${domainPart}`;
    });
  };

  const select = (lang) => {
    clearGoogtransCookies();
    if (lang.code !== "en") {
      setGoogtransCookies(`/en/${lang.code}`);
    }
    document.documentElement.dir = lang.dir;
    document.documentElement.lang = lang.code;
    setOpen(false);
    if (onSelect) onSelect();
    window.location.reload();
  };

  const currentLang = LANGS.find((l) => l.code === current) || LANGS[0];

  return (
    <div ref={ref} className={`relative notranslate ${mobile ? "w-full" : ""}`} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label="Change language"
        data-testid="language-switcher-button"
        className={`inline-flex items-center gap-2 rounded-full border border-navy-100 px-4 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy ${
          mobile ? "w-full justify-center py-3.5 text-base" : ""
        }`}
      >
        <Globe className="h-4 w-4 text-waratah" />
        {currentLang.label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
          role="menu"
          data-testid="language-menu"
        >
          {LANGS.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="menuitem"
              onClick={() => select(lang)}
              data-testid={`language-option-${lang.code.toLowerCase()}`}
              className={`block w-full px-5 py-3 text-left text-base transition-colors duration-150 hover:bg-surface ${
                lang.code === current ? "font-semibold text-waratah" : "text-navy"
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
